import { Stack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormModal } from '../../../components/FormModal'
import { ISelectOption } from '../../../components/Select'
import { TabErrorLabel } from '../../../components/TabErrorLabel'
import { useAppToast } from '../../../helpers'
import { countErrorsPerTab, firstTabWithErrors, withoutFieldErrors } from '../../../helpers/tabErrorUtils'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { IEmailCheckRaw, IEmployeeDetailRaw } from '../../../services/Employee/types'
import { JobPosition } from '../../../services/JobPosition/types'
import { TFormErrors } from '../Admission/admissionForm'
import { LegacyLinkField, SameEmailWarning } from './EmployeeActionModal'
import { EMPLOYEE_TAB_DEFS } from './employeeConstants'
import { EMPTY_EMPLOYEE_FORM, employeeFormData, IEmployeeForm } from './employeeForm'
import { employeeFormPanels, useUsernameSuggestions } from './EmployeeFormPanels'

interface IEmployeeCreateModalProps {
  isOpen: boolean
  onClose: () => void
  roles: JobPosition[]
  sectors: ISelectOption<number>[]
  onCreated: (employee: IEmployeeDetailRaw) => void
}

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * "Adicionar colaborador": a current collaborator registered by RH, straight to Ativos, with no
 * kanban card and no electronic acceptance. Name, e-mail, role and admission date are required;
 * the rest can be completed later on the sheet.
 */
export function EmployeeCreateModal({ isOpen, onClose, roles, sectors, onCreated }: IEmployeeCreateModalProps) {
  const toast = useAppToast()
  const [form, setForm] = useState<IEmployeeForm>(EMPTY_EMPLOYEE_FORM)
  const [photo, setPhoto] = useState<File | null>(null)
  const [errors, setErrors] = useState<TFormErrors>({})
  const [tabIndex, setTabIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [emailCheck, setEmailCheck] = useState<IEmailCheckRaw | null>(null)
  const [legacyIds, setLegacyIds] = useState<number[]>([])
  const usernames = useUsernameSuggestions(form.name, isOpen)

  useEffect(() => {
    if (!isOpen) return
    setForm(EMPTY_EMPLOYEE_FORM)
    setPhoto(null)
    setErrors({})
    setTabIndex(0)
    setEmailCheck(null)
    setLegacyIds([])
  }, [isOpen])

  // Legacy records to link and repeated e-mails, read a moment after the e-mail stops changing.
  useEffect(() => {
    const email = form.email.trim()
    if (!isOpen || !EMAIL_FORMAT.test(email)) {
      setEmailCheck(null)
      return
    }

    const timer = setTimeout(() => {
      new EmployeeService()
        .emailCheck(email)
        .then(setEmailCheck)
        .catch(() => setEmailCheck(null))
    }, 500)

    return () => clearTimeout(timer)
  }, [form.email, isOpen])

  const update = (patch: Partial<IEmployeeForm>) => {
    setForm((current) => ({ ...current, ...patch }))
    setErrors((current) => withoutFieldErrors(current, Object.keys(patch)))
  }

  const save = () => {
    const data = employeeFormData(form, photo, 'active')
    // Only the records listed for the e-mail as it is now.
    emailCheck?.legacy_matches
      .filter((match) => legacyIds.includes(match.id))
      .forEach((match) => data.append('link_legacy_ids[]', String(match.id)))

    setSaving(true)
    new EmployeeService()
      .create(data)
      .then((employee) => {
        toast.success(`${employee.name ?? 'Colaborador'} adicionado em Colaboradores Ativos`)
        onCreated(employee)
        onClose()
      })
      .catch((error) => {
        const fieldErrors = error?.response?.data?.errors
        if (fieldErrors) {
          setErrors(fieldErrors)
          const tab = firstTabWithErrors(EMPLOYEE_TAB_DEFS, fieldErrors)
          if (tab >= 0) setTabIndex(tab)
          toast.invalidForm()
          return
        }
        toast.error('Erro ao adicionar o colaborador', error)
      })
      .finally(() => setSaving(false))
  }

  const panels = employeeFormPanels({
    form,
    update,
    errors,
    isDisabled: saving,
    status: 'active',
    photo,
    photoUrl: null,
    onPhotoChange: setPhoto,
    roles,
    sectors,
    usernames
  })

  const errorCounts = countErrorsPerTab(EMPLOYEE_TAB_DEFS, errors)
  const tabLabel = (index: number) => (
    <TabErrorLabel label={EMPLOYEE_TAB_DEFS[index].label} count={errorCounts[index]} />
  )

  const emailNotices = emailCheck && (
    <Stack spacing={3} mb={emailCheck.legacy_matches.length || emailCheck.same_email.length ? 6 : 0}>
      <SameEmailWarning people={emailCheck.same_email} />
      <LegacyLinkField matches={emailCheck.legacy_matches} selected={legacyIds} onChange={setLegacyIds} />
    </Stack>
  )

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Adicionar colaborador"
      size="editor"
      onSubmit={save}
      confirmLabel="Adicionar"
      isLoading={saving}
      noValidate
      tabIndex={tabIndex}
      onTabChange={setTabIndex}
      tabs={[
        {
          label: tabLabel(0),
          content: (
            <>
              {emailNotices}
              {panels.personal}
            </>
          )
        },
        { label: tabLabel(1), content: panels.professional },
        { label: tabLabel(2), content: panels.contacts },
        { label: tabLabel(3), content: panels.benefits }
      ]}
    />
  )
}
